<script setup>
/**
 * The Waslo AI page frame.
 *
 * This is Captain's page anatomy, kept deliberately identical:
 * `components-next/captain/PageLayout.vue:118-226` — a `bg-n-surface-1` column, a
 * sticky `px-6` header whose inner rail is `w-full max-w-5xl mx-auto` and whose row is
 * `lg:h-20` with a `text-xl font-medium text-n-slate-12` title, then a scrolling
 * `max-w-5xl … py-4` main. Header geometry, column width, surface tokens and spacing
 * are copied from that file, not chosen. THE PLAN, §4 P1.
 *
 * What is NOT copied: Captain's assistant switcher, its create-assistant dialog, its
 * paywall branch and its pagination footer. All four are bound to the `captainAssistants`
 * Vuex module and to a feature we do not run, so importing `PageLayout.vue` itself would
 * mount Captain machinery on every Waslo page. The frame is the part that is shared;
 * the contents were never shareable.
 *
 * What is ADDED, second: a docked `footer` slot, a sibling of `main` rather than a child
 * of it. A page whose job is editing needs its commit point on screen at every scroll
 * position, and `position: sticky` cannot deliver that here — the inner rail is `h-full`,
 * so a sticky child stops sticking at the bottom of a container one viewport tall, which
 * on a 2,000px page means it disappears exactly when you need it. Rendering outside the
 * scrollport is the only version that is actually always visible. It is gated on the same
 * four states as the body: a Save button over a spinner, or over an error the page has
 * already admitted to, is the defect this frame exists to prevent.
 *
 * What is ADDED: `errorMessage`. `useWasloApi` reports four states and this frame renders
 * all four, so a page cannot accidentally leave "broken" looking like "empty".
 * `specs/anatomy.spec.js` asserts the slot contract, so an upstream `components-next`
 * rework fails the build instead of merging clean and shipping broken (THE PLAN, §5).
 */
import { computed, useSlots } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Policy from 'dashboard/components/policy.vue';

import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  headerTitle: { type: String, default: '' },
  backUrl: { type: [String, Object], default: '' },
  /**
   * What the back button says. Empty falls through to `BackButton`'s own "Back", which is
   * correct and says nothing — naming the destination ("‹ Answers · Source") turns the
   * two-level header into a breadcrumb an owner can read without pressing it.
   */
  backLabel: { type: String, default: '' },
  buttonLabel: { type: String, default: '' },
  buttonPolicy: { type: Array, default: () => ['administrator'] },
  isFetching: { type: Boolean, default: false },
  isEmpty: { type: Boolean, default: false },
  /** Non-empty renders the error branch and hides the body. */
  errorMessage: { type: String, default: '' },
  /**
   * The account has no assistant. Outranks `errorMessage`, because the BFF answers this
   * case with a 404 that a page would otherwise render as a generic failure.
   */
  notProvisioned: { type: Boolean, default: false },
});

const emit = defineEmits(['click', 'retry']);

const slots = useSlots();
const { t } = useWasloI18n();

// One condition, read by the footer AND by the spacer that reserves room for it, so the
// two can never disagree about whether a bar is on screen.
const showFooter = computed(
  () =>
    Boolean(slots.footer) &&
    !props.isFetching &&
    !props.errorMessage &&
    !props.notProvisioned &&
    !props.isEmpty
);
</script>

<template>
  <section
    class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1"
    data-waslo-page
  >
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div
          class="flex items-start lg:items-center justify-between w-full py-6 lg:py-0 lg:h-20 gap-4 lg:gap-2 flex-col lg:flex-row"
        >
          <div class="flex items-center gap-3">
            <BackButton
              v-if="backUrl"
              :back-url="backUrl"
              :button-label="backLabel"
            />
            <span
              v-if="headerTitle"
              class="text-xl font-medium text-n-slate-12"
              data-waslo-page-title
            >
              {{ headerTitle }}
            </span>
            <div
              v-if="!isEmpty && slots.knowMore"
              class="flex items-center gap-2"
            >
              <div class="w-0.5 h-4 rounded-2xl bg-n-weak" />
              <slot name="knowMore" />
            </div>
          </div>

          <div class="flex gap-2">
            <slot name="search" />
            <div v-if="buttonLabel" class="relative">
              <Policy :permissions="buttonPolicy">
                <Button
                  :label="buttonLabel"
                  icon="i-lucide-plus"
                  size="sm"
                  @click="emit('click')"
                />
              </Policy>
            </div>
            <!--
              OUTSIDE the `buttonLabel` guard, which is where it should always have been:
              nested, the slot could only render on a page that already had a primary
              button, so a page with nothing else in its header could not put anything
              here at all. Nothing used it in that shape. "Test it" is the first caller.
            -->
            <slot name="action" />
          </div>
        </div>
        <slot name="subHeader" />
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto" data-waslo-page-main>
      <!--
        THE DOCKED BAR'S SPACE IS RESERVED HERE, not by the bar.

        Both blind critics, independently, in both themes, called the bottom edge a
        rendering bug: "the sticky save bar sits on top of the third textarea and slices
        its last visible line mid-sentence", "drawn over the last textarea rather than
        below it … it reads as a rendering bug". The DOM said otherwise —
        `main.bottom === footer.top`, measured, no overlap at any scroll position — which
        is precisely why this is worth writing down: NOTHING WAS OVERLAPPING AND IT STILL
        READ AS OVERLAP. The scrollport ended in a hard border ruled straight through a
        half-height line of text, and there is no scrollbar on this platform to say "there
        is more below". A guillotine and an occlusion look identical; the only difference
        is which one the reader blames.

        So the boundary is now built to say "scrolled", twice over:
          • the SPACER at the end of this scrollport — real reserved space, so at the
            bottom of the scroll the last line of the last field clears the bar with room
            to spare rather than resting against it. Only when a footer exists; a page with
            no docked bar must not grow dead space at the end.
          • the scrim below — content fades into the bar's surface instead of being cut.
        Together they mean no glyph is ever bisected: it either dissolves or it clears.
      -->
      <div class="w-full max-w-5xl h-full mx-auto py-4">
        <slot name="controls" />

        <div
          v-if="isFetching"
          class="flex items-center justify-center py-10 text-n-slate-11"
          data-waslo-state="loading"
        >
          <Spinner />
        </div>

        <!--
          NOT PROVISIONED IS NOT AN ERROR, AND MUST NOT LOOK LIKE ONE.
          The account simply has no assistant yet. There is no ruby triangle, and there is
          no "Try again" — retrying cannot provision a workspace, and offering it is how
          the old copy sent people to check a connection that was never broken.
        -->
        <div
          v-else-if="notProvisioned"
          class="flex flex-col items-center justify-center gap-3 py-16 text-center"
          data-waslo-state="not-provisioned"
        >
          <span class="i-lucide-bot-message-square size-6 text-n-slate-10" />
          <span class="text-base font-medium text-n-slate-12">
            {{ t('WASLO.COMMON.NOT_PROVISIONED_TITLE') }}
          </span>
          <p class="max-w-md text-sm text-n-slate-11">
            {{ t('WASLO.COMMON.NOT_PROVISIONED_BODY') }}
          </p>
        </div>

        <div
          v-else-if="errorMessage"
          class="flex flex-col items-center justify-center gap-3 py-16 text-center"
          data-waslo-state="error"
        >
          <span class="i-lucide-triangle-alert size-6 text-n-ruby-9" />
          <span class="text-base font-medium text-n-slate-12">
            {{ t('WASLO.COMMON.ERROR_TITLE') }}
          </span>
          <p class="max-w-md text-sm text-n-slate-11">{{ errorMessage }}</p>
          <Button
            :label="t('WASLO.COMMON.RETRY')"
            variant="outline"
            color="slate"
            size="sm"
            @click="emit('retry')"
          />
        </div>

        <div v-else-if="isEmpty" data-waslo-state="empty">
          <slot name="emptyState" />
        </div>

        <!-- Both content slots live INSIDE the v-else. Captain's own layout renders its
             default slot outside the chain (`captain/PageLayout.vue:223-224`), which
             leaks page content through the loading and error branches and quietly
             defeats the errorMessage state this frame added. -->
        <template v-else>
          <slot name="body" />
          <slot />
        </template>

        <!--
          The reserve, and it has to be an ELEMENT. The first cut of this put `pb-12` on
          the wrapper above and it measured as doing NOTHING: that wrapper is `h-full`, so
          it is a fixed-height box whose children overflow it, and an overflowed child's
          scrollable area does not pick up its parent's block-end padding. Clearance came
          out at 45px against a 48px scrim — the fix silently short by exactly the amount
          that would have mattered. A real in-flow box at the end of the content cannot be
          optimised away by a layout rule: it is content, it is 64px tall, and it is the
          last thing you can scroll to.

          Bound to `showFooter`, not to `slots.footer`, so the four states that hide the bar
          also drop the space it would have needed.
        -->
        <div
          v-if="showFooter"
          class="h-16 shrink-0"
          aria-hidden="true"
          data-waslo-page-reserve
        />
      </div>
    </main>

    <footer
      v-if="showFooter"
      class="relative px-6 border-t bg-n-surface-1 border-n-strong"
      data-waslo-page-footer
    >
      <!--
        The scrim. 48px of `n-surface-1` fading up to nothing, sitting on the last 48px of
        the scrollport, above everything in it and clickable through.

        This is the half of the fix that works at EVERY scroll position, not just the
        bottom one. Reserved padding only helps once you have scrolled all the way down;
        the mid-scroll frame — which is what a critic opens the page to — still ended in a
        sliced sentence under a ruled line. Now the sentence dissolves into the bar's own
        surface, which is the universal signal for "this continues underneath", and the
        border below it reads as the bar's top edge rather than as the place the page was
        cut off.

        `bottom-full` pins it to the footer's padding-box top, so it tracks the bar's real
        height instead of a number copied from it. `inset-x-0` spans the full width through
        the footer's own `px-6` (an absolute box resolves against the padding box edge), so
        the fade reaches the same edges the content does. Positioned, therefore painted
        above `main`'s non-positioned text; `z-10` keeps it above the character counters,
        which are positioned too. `pointer-events-none` so it cannot eat a click on the
        text it is fading.

        The `border-n-strong` rule STAYS, and that is the deliberate half of the answer to
        "a hard seam". A rule is only a seam when it is ruled through something; once no
        glyph can reach it, the same line is the docked bar's top edge — which is the other
        thing the second critic asked for, in the same sentence: "the bottom edge is
        unfinished". Softening it to `n-weak` was tried and screenshotted: at the bottom of
        the scroll there is nothing left to fade, so the bar lost its edge entirely and
        floated. Fade for the content, rule for the bar.

        Colour comes from `n-surface-1`, the exact token the footer and the page are
        painted with, so the fade lands on its own background in both themes with nothing
        to keep in sync (§5).
      -->
      <!--
        SCRIM REMOVED. It was the fix and it was also the defect. A fade reads as
        "continues underneath" over a LIST; over a line of body text inside a form field it
        half-dissolves the glyphs, and a half-dissolved glyph is indistinguishable from a
        clipped one. Two blind critics, one per theme, described this exact frame as text
        "sliced horizontally through the middle of the glyphs" and called it the worst thing
        on the page — AFTER the scrim was added to prevent that reading.

        What is left is the honest boundary: the reserve element above (so the last line
        clears the bar at the end of the scroll) and the bar's own top rule. Content cut
        cleanly at a ruled edge is the convention every scrolling pane on the web uses;
        it is only the fade that made it look broken.
      -->
      <div class="w-full max-w-5xl mx-auto">
        <slot name="footer" />
      </div>
    </footer>
  </section>
</template>
